import { Lead } from '../types'
import * as API from './API'
import { parsePhoneNumberWithError, ParseError } from 'libphonenumber-js'

/**
 *
 */
export class ValidationError extends Error {
    errorCode: undefined | number;

    /**
     *
     */
    constructor (message: string, errorCode?: number) {
        super(message)
        this.errorCode = errorCode
    }
}

/**
 * Detects any problems in a lead, traversing the fields in order and raising the first error found
 */
export async function validateLead (leadData: Lead) {
    // Important validate fields from top to bottom as they would typically be filled in
    // This will either raise an error on the first detected error, or return on success
    const requiredFields = {
        company_name: 'Company name', // eslint-disable-line @typescript-eslint/naming-convention
        first_name: 'First name', // eslint-disable-line @typescript-eslint/naming-convention
        last_name: 'Last name', // eslint-disable-line @typescript-eslint/naming-convention
        email: 'Email',
        personal_phone_number: 'Mobile number', // eslint-disable-line @typescript-eslint/naming-convention
        password: 'Password',
        street: 'Billing address',
        city: 'City',
        country: 'Country'
    }

    const checkFieldsForBlankness = (fields: string[]) => {
        fields.forEach((field: string) => {
            if (/^\s*$/.test(leadData[field] || '')) {
                throw new ValidationError(`${requiredFields[field]} is required`)
            }
        })
    }

    const { personal_phone_number, email, password, state, country, billing_zip_code } = leadData // eslint-disable-line @typescript-eslint/naming-convention

    checkFieldsForBlankness(['company_name'])

    const nameFields = ['first_name', 'last_name']
    nameFields.forEach((field: string) => {
        checkFieldsForBlankness([field])
        if ((leadData[field] || '').length < 2) {
            throw new ValidationError(`${requiredFields[field]} must be at least 2 characters long`)
        }
    })

    validateEmail(email || '')

    validatePhoneNumber(personal_phone_number || '')

    validatePassword(password || '')

    checkFieldsForBlankness(['street', 'city'])

    checkFieldsForBlankness(['country'])

    if (!state) {
        throw new ValidationError(`Must select a ${leadData.country === 'Canada' ? 'province' : 'state'}`)
    }

    await validateZipCode(country || '', billing_zip_code || '')

    return leadData // no errors
}

function validateEmail (email: string) {
    if (!/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/.test(email)) {
        throw new ValidationError('Please enter a valid email address')
    }
    return null
}

/**
 * Returns true if the string follows the format of a valid email address
 */
export function isEmailValid (email: string) {
    try {
        validateEmail(email)
        return true
    } catch (error) {
        return false
    }
}

function validatePassword (password: string) {
    if (password.length < 8) {
        throw new ValidationError('Password must be at least 8 characters long')
    }

    if (!/[A-Z]+/.test(password)) {
        throw new ValidationError('Password must contain at least one capital letter')
    }

    if (!/[a-z]+/.test(password)) {
        throw new ValidationError('Password must contain at least one lower case letter')
    }
}

function validatePhoneNumber (number: string) {
    if (!number) {
        throw new ValidationError('Must enter a mobile number')
    }
    let parsedNumber
    try {
        parsedNumber = parsePhoneNumberWithError(number, 'US')
    } catch (error) {
        if (error instanceof ParseError) {
            // Not a phone number, non-existent country, etc.
            throw new ValidationError('Please enter a valid phone number')
        } else {
            throw error
        }
    }
    if (!parsedNumber || !parsedNumber.isValid()) {
        throw new ValidationError('Please enter a valid phone number')
    }
}

async function validateZipCode (country: string, zipCode: string) {
    const zipCodeVocab = country === 'Canada' ? 'postal code' : 'zip code'
    if (!zipCode) {
        throw new ValidationError(`Must enter a ${zipCodeVocab}`)
    }

    await API.checkZipCode(zipCode).catch(error => {
        if (error.message.toLowerCase().includes('validation errors')) { // the specific message here and in error.errors is less than ideal, so replace with a better error message
            throw new ValidationError(`The ${zipCodeVocab} you entered is not valid`)
        }
    })
}
